import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { SALES_ORDER_ITEM } from '../types/sales-order-item.js';
/**
 * Descriptor for the sales order line item (SalesOrder child entity).
 * Subcollection: org/{orgId}/sales-order/{salesOrderId}/item
 *
 * Resolve repository with context: { orgId, salesOrderId }.
 * Owned child — no lifecycle; audit via AUDIT_* (request 0235).
 */
export const salesOrderItemDescriptor = {
    name: SALES_ORDER_ITEM,
    label: 'Item do pedido',
    domain: 'sales',
    collectionPath: 'org/{orgId}/sales-order/{salesOrderId}/item',
    pathPatternKeys: {
        orgId: 'string',
        salesOrderId: 'string',
    },
    version: 1,
    description: 'Sales order line item referencing a Product, with computed totalPrice',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        // NoCache: filho de entidade versionada (spec 0045) — itens em cache voltariam velhos após Recarregar e seriam regravados por cima.
        cache: CacheStrategy.NoCache,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true, description: 'Unique item identifier' },
        {
            name: 'salesOrderId',
            label: 'Pedido',
            type: 'string',
            required: true,
            description: 'Owning order id (also encoded in the subcollection path)',
        },
        {
            name: 'productId',
            label: 'Produto',
            type: 'string',
            required: true,
            description: 'Reference to the product (ProductRef.productId)',
        },
        {
            name: 'variantId',
            label: 'Variante',
            type: 'string',
            required: false,
            description: 'Partial ProductVariant (identifying axes; ProductRef.variantId). Size lives on SalesOrderItemBreakdown.',
        },
        {
            name: 'description',
            example: 'Malha PV 30/1 Penteada - Vermelho Carmim',
            label: 'Descrição',
            type: 'string',
            required: true,
            description: 'Line description (snapshot of the product description at sale time)',
        },
        {
            name: 'quantity',
            example: 250,
            label: 'Quantidade',
            type: 'number',
            display: 'quantity',
            required: true,
            description: 'Quantity',
        },
        {
            name: 'enteredQuantity',
            example: 1700,
            label: 'Quantidade digitada',
            type: 'number',
            display: 'quantity',
            required: false,
            description: 'Quantity as typed in an alternate product unit (memory; base quantity remains authoritative)',
        },
        {
            name: 'enteredUnitId',
            label: 'Unidade digitada',
            type: 'string',
            required: false,
            description: 'FK ProductUnit used when typing enteredQuantity (spec 0050 D4)',
        },
        {
            name: 'unitPrice',
            example: 38.9,
            label: 'Preço unitário',
            type: 'number',
            display: 'money',
            required: true,
            description: 'Unit price at sale time',
        },
        {
            name: 'totalPrice',
            example: 9725,
            label: 'Total',
            type: 'number',
            display: 'money',
            required: false,
            description: 'Computed = quantity * unitPrice (service invariant)',
        },
        {
            name: 'shippedQuantity',
            example: 250,
            label: 'Expedido',
            type: 'number',
            display: 'quantity',
            required: false,
            description: 'Cumulative shipped qty (service-owned; request 0110)',
        },
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        {
            fieldName: 'productId',
            targetEntity: 'Product',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Produto vinculado ao item',
        },
        {
            fieldName: 'variantId',
            targetEntity: 'ProductVariant',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Variante parcial (identificadores; sem tamanho)',
        },
        {
            fieldName: 'enteredUnitId',
            targetEntity: 'ProductUnit',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Unidade do produto usada na digitação (memória)',
        },
        {
            fieldName: 'salesOrderId',
            targetEntity: 'SalesOrder',
            relationship: 'many-to-one',
            displayField: 'code',
            description: 'Pedido de origem',
        },
        ...AUDIT_FOREIGN_KEYS,
    ],
};
//# sourceMappingURL=sales-order-item.descriptor.js.map